import { 
  AppError, 
  createSystemError, 
  createAuthError, 
  createUsageError,
  createDocumentError,
  SYSTEM_ERRORS,
  AUTH_ERRORS,
  USAGE_ERRORS,
  DOCUMENT_ERRORS
} from '@nvgc/shared';
import { withRetry, RetryOptions } from './retryUtils';
import { errorReportingService } from '../services/errorReportingService';
import { SecurityUtils } from './security';

export interface ApiClientConfig {
  baseUrl: string;
  timeout: number;
  retryOptions: Partial<RetryOptions>;
  defaultHeaders: Record<string, string>;
}

export interface ApiResponse<T> {
  data: T;
  status: number;
  headers: Headers;
  ok: boolean;
}

const DEFAULT_CONFIG: ApiClientConfig = {
  baseUrl: import.meta.env.VITE_API_URL || '/api',
  timeout: 30000,
  retryOptions: {
    maxAttempts: 3,
    baseDelay: 1000,
    maxDelay: 10000,
    backoffFactor: 2
  },
  defaultHeaders: {
    'Content-Type': 'application/json'
  }
};

class ApiClient {
  private config: ApiClientConfig;
  private abortController: AbortController | null = null;

  constructor(config: Partial<ApiClientConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Make an HTTP request with error handling and retry logic
   */
  async request<T>(
    endpoint: string,
    options: RequestInit & {
      timeout?: number;
      retryOptions?: Partial<RetryOptions>;
      skipRetry?: boolean;
    } = {}
  ): Promise<ApiResponse<T>> {
    const {
      timeout = this.config.timeout,
      retryOptions = this.config.retryOptions,
      skipRetry = false,
      ...fetchOptions
    } = options;

    const url = `${this.config.baseUrl}${endpoint}`;
    
    // Create abort controller for this request
    this.abortController = new AbortController();
    
    const requestOptions: RequestInit = {
      ...fetchOptions,
      headers: {
        ...this.config.defaultHeaders,
        ...fetchOptions.headers
      },
      signal: this.abortController.signal
    };

    // Add auth token if available
    const token = this.getAuthToken();
    if (token) {
      (requestOptions.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }
    
    // Add CSRF token for state-changing operations
    const method = requestOptions.method?.toUpperCase();
    if (method && !['GET', 'HEAD', 'OPTIONS'].includes(method)) {
      try {
        const csrfHeaders = await SecurityUtils.addCSRFHeaders(requestOptions.headers as Record<string, string>);
        requestOptions.headers = csrfHeaders;
      } catch (error) {
        console.warn('Failed to add CSRF token:', error);
      }
    }

    const makeRequest = async (): Promise<ApiResponse<T>> => {
      const timeoutId = setTimeout(() => {
        if (this.abortController) {
          this.abortController.abort();
        }
      }, timeout);

      try {
        const response = await fetch(url, requestOptions);
        clearTimeout(timeoutId);

        if (!response.ok) {
          throw await this.createErrorFromResponse(response);
        }

        const data = await response.json();
        return {
          data,
          status: response.status,
          headers: response.headers,
          ok: response.ok
        };
      } catch (error) {
        clearTimeout(timeoutId);
        
        if (error instanceof Error && error.name === 'AbortError') {
          throw createSystemError(
            SYSTEM_ERRORS.TIMEOUT,
            { timeout, url },
            'Request timed out. Please try again.'
          );
        }

        if (error && typeof error === 'object' && 'code' in error && 'message' in error) {
          throw error;
        }

        // Network or other errors
        throw createSystemError(
          SYSTEM_ERRORS.NETWORK_ERROR,
          { originalError: error instanceof Error ? error.message : String(error), url },
          'Network error. Please check your connection and try again.'
        );
      }
    };

    try {
      if (skipRetry) {
        return await makeRequest();
      }

      return await withRetry(makeRequest, {
        ...retryOptions,
        onRetry: (attempt, error) => {
          console.log(`API retry attempt ${attempt} for ${url}:`, error);
          
          // Report retry attempts for monitoring
          errorReportingService.reportError(
            (error && typeof error === 'object' && 'code' in error && 'message' in error) ? error : createSystemError(SYSTEM_ERRORS.NETWORK_ERROR),
            errorReportingService.createContext('ApiClient', 'retry', {
              url,
              attempt,
              maxAttempts: retryOptions.maxAttempts
            })
          );
        }
      });
    } catch (error) {
      // Report final error
      const appError = (error && typeof error === 'object' && 'code' in error && 'message' in error) ? error as AppError : createSystemError(SYSTEM_ERRORS.NETWORK_ERROR);
      errorReportingService.reportError(
        appError,
        errorReportingService.createContext('ApiClient', 'request', { url, method: requestOptions.method })
      );
      
      throw error;
    }
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, data?: any, options?: RequestInit): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, data?: any, options?: RequestInit): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  /**
   * Upload file with progress tracking
   */
  async uploadFile<T>(
    endpoint: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<T>> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const formData = new FormData();
      formData.append('file', file);

      // Add auth token
      const token = this.getAuthToken();
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      
      // Add CSRF token for uploads
      SecurityUtils.addCSRFHeaders().then(headers => {
        const csrfToken = headers['X-CSRF-Token'];
        if (csrfToken) {
          xhr.setRequestHeader('X-CSRF-Token', csrfToken);
        }
      }).catch(error => {
        console.warn('Failed to add CSRF token to upload:', error);
      });

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = (event.loaded / event.total) * 100;
          onProgress(progress);
        }
      });

      xhr.addEventListener('load', async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve({
              data,
              status: xhr.status,
              headers: new Headers(), // XMLHttpRequest doesn't provide easy access to response headers
              ok: true
            });
          } catch (error) {
            reject(createSystemError(
              SYSTEM_ERRORS.SERVER_ERROR,
              { parseError: error },
              'Failed to parse server response.'
            ));
          }
        } else {
          reject(await this.createErrorFromXHR(xhr));
        }
      });

      xhr.addEventListener('error', () => {
        reject(createSystemError(
          SYSTEM_ERRORS.NETWORK_ERROR,
          { status: xhr.status },
          'Upload failed. Please check your connection and try again.'
        ));
      });

      xhr.addEventListener('timeout', () => {
        reject(createSystemError(
          SYSTEM_ERRORS.TIMEOUT,
          { timeout: this.config.timeout },
          'Upload timed out. Please try again.'
        ));
      });

      xhr.timeout = this.config.timeout;
      xhr.open('POST', `${this.config.baseUrl}${endpoint}`);
      xhr.send(formData);
    });
  }

  /**
   * Cancel current request
   */
  cancel(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  /**
   * Create AppError from fetch Response
   */
  private async createErrorFromResponse(response: Response): Promise<AppError> {
    let errorData: any = {};
    
    try {
      errorData = await response.json();
    } catch {
      // Response doesn't contain JSON
    }

    const status = response.status;
    // const errorCode = errorData.code || `HTTP_${status}`;
    const message = errorData.message || response.statusText || 'Request failed';

    // Map HTTP status codes to appropriate error types
    switch (status) {
      case 401:
        return createAuthError(
          AUTH_ERRORS.UNAUTHORIZED,
          { status, response: errorData },
          'Please log in to continue.'
        );
      
      case 403:
        return createAuthError(
          AUTH_ERRORS.INSUFFICIENT_PERMISSIONS,
          { status, response: errorData },
          'You don\'t have permission to perform this action.'
        );
      
      case 429:
        return createUsageError(
          USAGE_ERRORS.RATE_LIMIT,
          { status, response: errorData },
          'Too many requests. Please wait a moment and try again.'
        );
      
      case 402:
        return createUsageError(
          USAGE_ERRORS.PAYMENT_REQUIRED,
          { status, response: errorData },
          'Payment required to continue. Please update your billing information.'
        );
      
      case 413:
        return createDocumentError(
          DOCUMENT_ERRORS.FILE_TOO_LARGE,
          { status, response: errorData },
          'File is too large. Please upload a smaller file.'
        );
      
      case 415:
        return createDocumentError(
          DOCUMENT_ERRORS.UNSUPPORTED_FORMAT,
          { status, response: errorData },
          'Unsupported file format. Please upload a PDF, DOCX, PNG, or JPG file.'
        );
      
      case 422:
        return createSystemError(
          SYSTEM_ERRORS.VALIDATION_ERROR,
          { status, response: errorData },
          'Invalid input. Please check your data and try again.'
        );
      
      case 500:
      case 502:
      case 503:
      case 504:
        return createSystemError(
          SYSTEM_ERRORS.SERVER_ERROR,
          { status, response: errorData },
          'Server error. Please try again in a moment.'
        );
      
      default:
        return createSystemError(
          SYSTEM_ERRORS.SERVER_ERROR,
          { status, response: errorData },
          message
        );
    }
  }

  /**
   * Create AppError from XMLHttpRequest
   */
  private async createErrorFromXHR(xhr: XMLHttpRequest): Promise<AppError> {
    let errorData: any = {};
    
    try {
      errorData = JSON.parse(xhr.responseText);
    } catch {
      // Response doesn't contain JSON
    }

    const status = xhr.status;
    const message = errorData.message || xhr.statusText || 'Upload failed';

    return createDocumentError(
      DOCUMENT_ERRORS.UPLOAD_FAILED,
      { status, response: errorData },
      message
    );
  }

  /**
   * Get authentication token from Supabase session
   */
  private getAuthToken(): string | null {
    // Get the Supabase URL to extract the project ID
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://wilvfntdsuhhtmlcqohw.supabase.co';
    const projectId = supabaseUrl.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1];
    
    if (projectId) {
      // Try the specific project key first
      const projectKey = `sb-${projectId}-auth-token`;
      const projectToken = localStorage.getItem(projectKey);
      if (projectToken) {
        try {
          const parsed = JSON.parse(projectToken);
          return parsed?.access_token || null;
        } catch {
          // Continue to fallback
        }
      }
    }
    
    // Fallback: Check for any Supabase session in localStorage
    const storageKeys = Object.keys(localStorage);
    const supabaseKey = storageKeys.find(key => key.startsWith('sb-') && key.endsWith('-auth-token'));
    
    if (supabaseKey) {
      try {
        const sessionData = JSON.parse(localStorage.getItem(supabaseKey) || '{}');
        return sessionData?.access_token || null;
      } catch {
        return null;
      }
    }
    
    return null;
  }
}

// Create singleton instance
export const apiClient = new ApiClient();

// Export convenience methods
export const api = {
  get: <T>(endpoint: string, options?: RequestInit) => apiClient.get<T>(endpoint, options),
  post: <T>(endpoint: string, data?: any, options?: RequestInit) => apiClient.post<T>(endpoint, data, options),
  put: <T>(endpoint: string, data?: any, options?: RequestInit) => apiClient.put<T>(endpoint, data, options),
  delete: <T>(endpoint: string, options?: RequestInit) => apiClient.delete<T>(endpoint, options),
  upload: <T>(endpoint: string, file: File, onProgress?: (progress: number) => void) => 
    apiClient.uploadFile<T>(endpoint, file, onProgress),
  cancel: () => apiClient.cancel()
};